import React from 'react';
import { motion } from 'motion/react';
import {
  Coins,
  Zap,
  Clock,
  Shield,
  Calculator,
  TrendingUp,
  FileText,
  Gift,
  Headphones,
  Globe,
  Wallet,
  CreditCard,
  Smartphone,
  Building,
  PiggyBank,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Check,
  MessageCircle,
  Phone,
  HelpCircle,
  Languages,
  Fingerprint,
  Lock,
  History,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PDFDownloadButton from '../components/PDFDownloadButton';
import '../styles/fyntloan.css';

const mainFeatures = [
  {
    icon: Zap,
    title: 'Quick & Easy Application',
    description: 'Apply for loans in minutes. Select your amount, choose a repayment term (1-12 months), and submit. Get instant approval decisions without lengthy paperwork.',
    color: 'pink',
  },
  {
    icon: Shield,
    title: 'Flexible Collateral Options',
    description: 'Secure your loan with various collateral types including property, vehicles, savings, or other valuable assets. We work with what you have.',
    color: 'purple',
  },
  {
    icon: Wallet,
    title: 'Multiple Payment Methods',
    description: 'Repay your loan conveniently through mobile money, bank transfer, or card payments. Pay your monthly installment or settle your full balance with just a few taps.',
    color: 'blue',
  },
  {
    icon: BarChart3,
    title: 'Real-Time Loan Tracking',
    description: 'Track all your active loans and monitor your complete repayment history. Stay on top of your finances with detailed dashboards and notifications.',
    color: 'green',
  },
  {
    icon: Calculator,
    title: 'Built-in Loan Calculator',
    description: 'Plan your finances before borrowing. Calculate your monthly payments, total interest, and full repayment amount based on your preferred loan terms.',
    color: 'orange',
  },
  {
    icon: TrendingUp,
    title: 'Credit Score Monitoring',
    description: 'Track your creditworthiness with our integrated credit score system. Understand the factors affecting your score and improve your financial health.',
    color: 'teal',
  },
];

const additionalFeatures = [
  {
    icon: Fingerprint,
    title: 'Digital Document Signing',
    description: 'Sign loan agreements electronically. No printing or scanning - simply sign with your finger on your device.',
  },
  {
    icon: Gift,
    title: 'Personalized Loan Offers',
    description: 'Receive pre-approved offers based on your profile. Access exclusive rates and higher limits as a trusted customer.',
  },
  {
    icon: Lock,
    title: 'Secure Document Storage',
    description: 'All your documents are securely stored and processed with bank-level encryption.',
  },
  {
    icon: Languages,
    title: 'Multi-Language Support',
    description: 'Use the app in your preferred language for a comfortable experience.',
  },
];

const supportFeatures = [
  { icon: HelpCircle, title: 'In-App FAQ', desc: 'Find answers instantly' },
  { icon: Phone, title: 'Phone Support', desc: 'Direct line to agents' },
  { icon: MessageCircle, title: 'Live Chat', desc: 'Real-time assistance' },
  { icon: Clock, title: '24/7 Available', desc: 'Help anytime you need' },
];

const paymentMethods = [
  { icon: Smartphone, name: 'Mobile Money', desc: 'MTN, Airtel, etc.' },
  { icon: Building, name: 'Bank Transfer', desc: 'Direct bank payments' },
  { icon: CreditCard, name: 'Card Payments', desc: 'Visa, Mastercard' },
  { icon: Wallet, name: 'Digital Wallets', desc: 'E-wallet support' },
];

const FyntLoanPage = () => {
  return (
    <div className="fynt-page" id="fyntloan-content">
      <Header />
      
      {/* Hero Section */}
      <section className="fynt-hero">
        <div className="container-custom">
          <div className="fynt-hero-wrapper">
            <div className="fynt-hero-content">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="fynt-badge"
              >
                <Coins size={16} />
                Fast & Secure Digital Lending
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                Fynt<span className="text-gradient">Quick</span> Loan
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="fynt-subtitle"
              >
                Get the funds you need in minutes, not days. Quick applications, 
                instant approvals, and flexible repayment options designed for your lifestyle.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="fynt-hero-buttons"
              >
                <a href="#contact" className="btn-fynt-primary">
                  Apply Now <ArrowRight size={16} />
                </a>
                <a href="#features" className="btn-fynt-secondary">
                  Learn More
                </a>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="hero-highlights"
              >
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>Instant Approval</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>1-12 Month Terms</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>No Hidden Fees</span>
                </div>
              </motion.div>
            </div>
            
            {/* App Screenshot */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="fynt-hero-image"
            >
              <div className="phone-mockup">
                <img 
                  src="https://customer-assets.emergentagent.com/job_omnisupport-2/artifacts/8d8q8dsu_f6ec3c7c8649a1618f49eb24db93ae1084c885cca333e8c5434aeef2646639ba.jpeg"
                  alt="FyntQuick Loan App Dashboard"
                />
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="fynt-hero-bg">
          <div className="fynt-blob-1" />
          <div className="fynt-blob-2" />
          <div className="fynt-circles" />
        </div>
      </section>
      
      {/* Main Features */}
      <section className="fynt-features" id="features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-fynt"
          >
            <span className="section-tag-fynt">Key Features</span>
            <h2>Everything You Need to Borrow Smart</h2>
            <p>A complete digital lending solution designed for speed, security, and convenience</p>
          </motion.div>
          
          <div className="features-grid-fynt">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`feature-card-fynt ${feature.color}`}
              >
                <div className="feature-icon-fynt">
                  <feature.icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Payment Methods */}
      <section className="fynt-payments">
        <div className="container-custom">
          <div className="payments-content">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="payments-text"
            >
              <span className="section-tag-fynt light">Payment Options</span>
              <h2>Repay Your Way</h2>
              <p>
                Choose the payment method that works best for you. Whether you prefer 
                mobile money, bank transfers, or card payments, we've got you covered.
              </p>
              <ul className="payments-benefits">
                <li><Check size={18} /> No transaction fees on repayments</li>
                <li><Check size={18} /> Automatic payment reminders</li>
                <li><Check size={18} /> Early repayment options</li>
                <li><Check size={18} /> Flexible due dates</li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="payments-grid"
            >
              {paymentMethods.map((method, index) => (
                <div key={method.name} className="payment-card">
                  <method.icon size={32} />
                  <h4>{method.name}</h4>
                  <p>{method.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Additional Features */}
      <section className="fynt-additional">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-fynt"
          >
            <span className="section-tag-fynt">More Features</span>
            <h2>Built for Your Convenience</h2>
            <p>Additional tools and features to make your borrowing experience seamless</p>
          </motion.div>
          
          <div className="additional-grid-fynt">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="additional-card-fynt"
              >
                <div className="additional-icon">
                  <feature.icon size={24} />
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Support Section */}
      <section className="fynt-support">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="support-content"
          >
            <div className="support-text">
              <span className="section-tag-fynt light">Customer Support</span>
              <h2>24/7 Support When You Need It</h2>
              <p>
                Our dedicated support team is available around the clock to help you 
                with any questions about your loan, payments, or account.
              </p>
            </div>
            
            <div className="support-grid">
              {supportFeatures.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="support-item"
                >
                  <item.icon size={24} />
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="fynt-how">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-fynt"
          >
            <span className="section-tag-fynt">How It Works</span>
            <h2>Get Your Loan in 4 Easy Steps</h2>
            <p>From application to funds in your account - it's that simple</p>
          </motion.div>
          
          <div className="steps-grid">
            {[
              { step: '01', title: 'Download & Register', desc: 'Create your account in minutes with basic information' },
              { step: '02', title: 'Apply for Loan', desc: 'Select amount, term, and submit your application' },
              { step: '03', title: 'Get Approved', desc: 'Receive instant decision on your loan request' },
              { step: '04', title: 'Receive Funds', desc: 'Money transferred directly to your account' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="step-card"
              >
                <span className="step-number">{item.step}</span>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="fynt-cta" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="cta-content-fynt"
          >
            <h2>Ready to Get Started?</h2>
            <p>Apply for your loan today and get funds when you need them most.</p>
            <div className="cta-buttons-fynt">
              <a href="mailto:sales@voxitconsulting.media" className="btn-fynt-primary btn-white">
                Apply Now
              </a>
              <a href="mailto:sales@voxitconsulting.media" className="btn-fynt-secondary btn-outline-white">
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
      <PDFDownloadButton 
        contentId="fyntloan-content" 
        fileName="VoxIT-FyntQuick-Loan" 
        buttonText="Download PDF"
      />
    </div>
  );
};

export default FyntLoanPage;
